import React, { useCallback, useEffect, useState } from "react";

import { faExclamationTriangle } from "@performyard/fontawesome/pro-solid-svg-icons";
import { PageBanner } from "@performyard/py-components";

import { useAdminSettingsHomeBranding } from "../../../../api/hooks";

import { isFeatureNotEnabledError } from "../featureFlagError";

const ROLLOUT_DEADLINE_LABEL = "June 20, 2026";

const dismissalKey = (companyId?: string) => `admin-home:nav-rollout-banner:dismissed:${companyId ?? "self"}`;

const readDismissed = (companyId?: string): boolean => {
	if (typeof window === "undefined") return false;
	try {
		return window.localStorage.getItem(dismissalKey(companyId)) === "true";
	} catch {
		return false;
	}
};

const writeDismissed = (companyId?: string) => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(dismissalKey(companyId), "true");
	} catch {
		// Storage may be unavailable (private mode / quota); banner just reappears next mount.
	}
};

type Props = {
	canViewBranding: boolean;
	companyId?: string;
	onViewSetting: () => void;
};

const NewNavigationRolloutBanner: React.FC<Props> = ({ canViewBranding, companyId, onViewSetting }) => {
	const { data, error } = useAdminSettingsHomeBranding(companyId);
	const [dismissed, setDismissed] = useState(() => readDismissed(companyId));

	useEffect(() => {
		setDismissed(readDismissed(companyId));
	}, [companyId]);

	const handleDismiss = useCallback(() => {
		writeDismissed(companyId);
		setDismissed(true);
	}, [companyId]);

	if (!canViewBranding) return null;
	if (isFeatureNotEnabledError(error)) return null;
	if (!data) return null;
	if (!data.tenantFlags.sideNavigationEnabled) return null;
	if (data.preferredNavigation !== "classic") return null;
	if (dismissed) return null;

	return (
		<PageBanner
			data-testid="new-navigation-rollout-banner"
			color="primary"
			size="large"
			icon={faExclamationTriangle}
			mainTitle="A new navigation is rolling out"
			subTitle={`Switch your team anytime before ${ROLLOUT_DEADLINE_LABEL} · Reversible during the trial`}
			rightActionButton={{
				children: "View Setting",
				onClick: onViewSetting,
			}}
			onDismiss={handleDismiss}
		/>
	);
};

export default NewNavigationRolloutBanner;
